import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers'
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/dom'
import { move } from '@dnd-kit/helpers'
import { DragDropProvider } from '@dnd-kit/react'
import { Button, Collapse, Empty, Flex, Typography } from 'antd'
import type { CollapseProps } from 'antd'
import { ChevronRight, Eye, EyeOff } from 'lucide-react'
import { useLayoutEffect, useState } from 'react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { CountBadge } from 'src/components/CountBadge'
import { IconWithBackground } from 'src/components/IconWithBackground'
import { useIconSizes, useScrollFade } from 'src/hooks'

import { ColumnSettingsRow } from '../ColumnSettingsRow/ColumnSettingsRow'
import { ColumnSettingsRowList } from '../ColumnSettingsRowList/ColumnSettingsRowList'
import { HIDDEN_SECTION, SHOWN_SECTION } from '../config'
import type {
  ColumnPinSide,
  ColumnSettingsConfig,
  ColumnSettingsHiddenGroup,
  ColumnSettingsItem,
  ColumnStateMap
} from '../types'
import { keptOnHideAll, matchesColumnSearch, pinSideOf } from '../utils'
import type { ColumnSettingsSplit } from '../utils'

import { useStyles } from './styles'
import { hiddenSectionOf } from './utils'

type GroupKeys = Record<string, string[]>

const SECTION_ICON_SIZE = 10
const EXPAND_ICON_GAP_RATIO = 0.75

type Props = {
  split: ColumnSettingsSplit
  columnsMap: ColumnStateMap | undefined
  query: string
  icons: ColumnSettingsConfig['icons']
  hints?: ColumnSettingsConfig['hints']
  hiddenGroups: ColumnSettingsHiddenGroup[]
  pinnable?: boolean
  keepOneOnHideAll?: boolean
  onToggle: (key: string, isShown: boolean) => void
  onPin: (key: string, side: ColumnPinSide | undefined) => void
  onHideAll: () => void
  onShowColumns: (keys: string[]) => void
  onMove: (shownKeys: string[], hiddenKeys: string[]) => void
}

const keysOf = (items: ColumnSettingsItem[]) => items.map(({ key }) => key)

export const ColumnSettingsSections = ({
  split,
  columnsMap,
  query,
  icons,
  hints,
  hiddenGroups,
  pinnable = true,
  keepOneOnHideAll = true,
  onToggle,
  onPin,
  onHideAll,
  onShowColumns,
  onMove
}: Props) => {
  const { t } = useTranslation()
  const { styles, cx, theme } = useStyles()
  const iconSizes = useIconSizes()
  const { ref: scrollFadeRef } = useScrollFade()
  const [draggedGroups, setDraggedGroups] = useState<GroupKeys>()
  const [draggedKey, setDraggedKey] = useState<string>()
  const [scroller, setScroller] = useState<HTMLElement | null>(null)

  useLayoutEffect(() => scrollFadeRef(scroller), [scrollFadeRef, scroller])

  const isSearching = query.trim() !== ''
  const itemsByKey = new Map(
    [...split.shown, ...split.hidden].map((item) => [item.key, item])
  )
  const hiddenSections = [
    ...hiddenGroups,
    {
      key: HIDDEN_SECTION,
      title: t('column_settings_hidden'),
      icon: EyeOff,
      columnKeys: []
    }
  ]
  const settledGroups: GroupKeys = {
    [SHOWN_SECTION]: keysOf(split.shown),
    ...Object.fromEntries(
      hiddenSections.map(({ key }) => [
        key,
        keysOf(
          split.hidden.filter(
            (item) =>
              hiddenSectionOf(item.key, hiddenGroups, HIDDEN_SECTION) === key
          )
        )
      ])
    )
  }
  const groups = draggedGroups ?? settledGroups
  const rowsOf = (group: string) =>
    groups[group].flatMap((key) => {
      const item = itemsByKey.get(key)

      return item && matchesColumnSearch(item, query) ? [item] : []
    })

  const shown = rowsOf(SHOWN_SECTION)
  const canHideAny =
    split.shown.length > keptOnHideAll(split.shown, keepOneOnHideAll).length

  if (
    isSearching &&
    Object.keys(groups).every((group) => !rowsOf(group).length)
  ) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('column_settings_not_found')}
      />
    )
  }

  const onDragStart = ({ operation }: DragStartEvent) => {
    setDraggedGroups(settledGroups)
    setDraggedKey(operation.source ? String(operation.source.id) : undefined)
  }

  const onDragOver = (event: DragOverEvent) => {
    event.preventDefault()
    setDraggedGroups((current) => current && move(current, event))
  }

  const onDragEnd = (event: DragEndEvent) => {
    const finalGroups = draggedGroups && move(draggedGroups, event)

    setDraggedGroups(undefined)
    setDraggedKey(undefined)

    if (event.canceled || !finalGroups) {
      return
    }

    onMove(
      finalGroups[SHOWN_SECTION],
      hiddenSections.flatMap(({ key }) => finalGroups[key])
    )
  }

  const onSectionAction =
    (action: () => void) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      action()
    }

  const sections = [
    {
      key: SHOWN_SECTION,
      title: t('column_settings_shown'),
      icon: Eye,
      color: theme.colorPrimary,
      rows: shown,
      isShown: true,
      action: {
        label: t('column_settings_hide_all'),
        disabled: !canHideAny,
        onClick: onHideAll
      }
    },
    ...hiddenSections.map(({ key, title, icon, color }) => ({
      key,
      title,
      icon: icon ?? EyeOff,
      color,
      rows: rowsOf(key),
      isShown: false,
      action: {
        label: t('column_settings_show_all'),
        disabled: !settledGroups[key].length,
        onClick: () => onShowColumns(settledGroups[key])
      }
    }))
  ].filter(
    ({ key, rows }) => rows.length || (!isSearching && key === SHOWN_SECTION)
  )

  const items: CollapseProps['items'] = sections.map(
    ({ key, title, icon: Icon, color, rows, isShown, action }) => ({
      key,
      label: (
        <Flex align="center" gap="small">
          <IconWithBackground size={SECTION_ICON_SIZE} color={color}>
            <Icon size={SECTION_ICON_SIZE} />
          </IconWithBackground>
          <Typography.Text strong>{title}</Typography.Text>
          <CountBadge
            showZero
            count={rows.length}
            color={theme.colorTextSecondary}
            indicatorStyle={{
              minWidth: theme.controlHeightXS,
              paddingInline: theme.paddingXXS,
              borderRadius: theme.controlHeightXS,
              background: theme.colorFillTertiary,
              fontVariantNumeric: 'tabular-nums'
            }}
          />
        </Flex>
      ),
      extra: (
        <Button
          type="link"
          size="small"
          disabled={action.disabled}
          onClick={onSectionAction(action.onClick)}
        >
          {action.label}
        </Button>
      ),
      classNames: { header: styles.header },
      children: (
        <ColumnSettingsRowList
          rows={rows}
          scrollElement={scroller}
          draggedKey={draggedKey}
          renderRow={(item, rowIndex) => (
            <ColumnSettingsRow
              key={item.key}
              item={item}
              index={rowIndex}
              group={key}
              icon={icons?.[item.key]}
              hint={hints?.[item.key]}
              isShown={isShown}
              isSortable={isShown && !isSearching}
              pinSide={pinSideOf(item, columnsMap)}
              pinnable={pinnable}
              onToggle={onToggle}
              onPin={onPin}
            />
          )}
        />
      )
    })
  )

  return (
    <DragDropProvider
      modifiers={[RestrictToVerticalAxis]}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <Flex ref={setScroller} vertical className={styles.scroller}>
        <Collapse
          ghost
          size="small"
          defaultActiveKey={[
            SHOWN_SECTION,
            ...hiddenSections.map(({ key }) => key)
          ]}
          expandIcon={({ isActive }) => (
            <ChevronRight
              size={iconSizes.small}
              className={cx(styles.chevron, isActive && styles.chevronOpen)}
            />
          )}
          styles={{
            header: {
              position: 'sticky',
              top: 0,
              zIndex: 1,
              alignItems: 'center',
              marginInline: theme.marginXXS,
              paddingBlock: 0,
              paddingInline: theme.paddingXS
            },
            body: { paddingBlock: theme.paddingXXS, paddingInline: 0 },
            icon: {
              marginInlineStart: 0,
              marginInlineEnd: theme.paddingXS * EXPAND_ICON_GAP_RATIO
            }
          }}
          items={items}
        />
      </Flex>
    </DragDropProvider>
  )
}
